import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import sessionLogger from '../services/sessionLogger';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

// Role mapping and constants (mirroring your Streamlit constants)
const ROLE_TAB_MAPPING = {
  'Basic': ['Search', 'Map', 'Lane Pricing', 'Rate Insights', 'Broker Metrics', 'Profile'],
  'Standard': ['Search', 'Map', 'Lane Pricing', 'Rate Insights', 'Broker Metrics', 'Profile'],
  'Premium': ['Search', 'Map', 'Lane Pricing', 'Profile', 'Route Optimization', 'Rate Insights', 'Broker Metrics'],
  'Admin': ['Search', 'Map', 'Lane Pricing', 'Profile', 'Route Optimization', 'Rate Insights', 'Broker Metrics', 'Admin'],
  'Dev': ['Search', 'Map', 'Lane Pricing', 'Profile', 'Route Optimization', 'Rate Insights', 'Broker Metrics', 'Admin', 'Testing']
};

// Valid plan descriptions from tai-khoan (no need to match Stripe price IDs)
const VALID_PLAN_DESCRIPTIONS = ['Standard', 'Premium'];

export const UserProvider = ({ children }) => {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [userData, setUserData] = useState(null);
  const [userRole, setUserRole] = useState('Basic');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debugInfoVisible, setDebugInfoVisible] = useState(true);

  // Add keyboard shortcut to toggle debug info (Ctrl+D)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.key === 'd' && process.env.NODE_ENV === 'development') {
        event.preventDefault();
        setDebugInfoVisible(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Real API call to get user data using Auth0 user info
  const fetchUserData = async (accessToken) => {
    try {
      console.log('Fetching real user data from API using Auth0 user info...');
      
      // Determine environment and use appropriate URL (similar to apiService.js)
      const isLocal = window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1' || 
                      process.env.NODE_ENV === 'development';
      
      const baseUrl = isLocal 
        ? (process.env.REACT_APP_TK_URL_TEST || '/tai-khoan')
        : '/tai-khoan'; // Use nginx proxy to tai-khoan service in production
      
      console.log('Environment detection:', {
        hostname: window.location.hostname,
        nodeEnv: process.env.NODE_ENV,
        isLocal,
        baseUrl
      });
      
      // Construct URL properly for both absolute and relative paths
      const fullUrl = baseUrl.startsWith('http') 
        ? `${baseUrl}/getuser`
        : `${window.location.origin}${baseUrl}/getuser`;
      
      const url = new URL(fullUrl);
      
      // Use real Auth0 user data instead of hardcoded values
      url.searchParams.append('user_id', user.sub); // Auth0 user ID
      url.searchParams.append('user_name', user.name || user.nickname || '');
      url.searchParams.append('user_email', user.email || '');

      console.log('User API call with Auth0 data:', {
        user_id: user.sub,
        user_name: user.name || user.nickname,
        user_email: user.email,
        url: url.toString()
      });

      // Increase timeout significantly - tai-khoan seems slow
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout - tai-khoan is slow
      
      console.log('[UserContext] Fetching from tai-khoan (15s timeout)...');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        mode: 'cors',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const userData = await response.json();
      console.log('[UserContext] ✅ Tai-khoan API response received:', {
        user_role: userData.user_role,
        plan_status: userData.plan_status,
        email: userData.email,
        fullResponse: userData
      });
      return userData;
    } catch (error) {
      console.error('Error fetching real user data:', error);
      throw error;
    }
  };

  // Determine user role - uses plan_desc from tai-khoan directly
  const determineUserRole = (userResponse, roleOverride = '') => {
    // Start with override or database role
    let role = roleOverride || userResponse.user_role || 'Basic';
    const planStatus = userResponse.plan_status;
    const planDesc = userResponse.plan_desc;

    console.log('Determining user role:', {
      databaseRole: userResponse.user_role,
      planStatus,
      planDesc,
      roleOverride,
      currentRole: role
    });

    // Override user role with plan_desc from tai-khoan if active subscription AND not Admin/Dev
    if (planStatus === 'active' && !['Admin', 'Dev'].includes(role)) {
      if (VALID_PLAN_DESCRIPTIONS.includes(planDesc)) {
        role = planDesc; // Use plan_desc directly (Standard or Premium)
        console.log(`Overriding role to ${planDesc} due to active subscription`);
      }
    } else if (['Admin', 'Dev'].includes(role)) {
      console.log(`Protected role ${role} - not overriding with subscription`);
    }

    console.log('Final user role:', role);
    return role;
  };

  // Get allowed tabs for user role
  const getAllowedTabs = (role) => {
    return ROLE_TAB_MAPPING[role] || ROLE_TAB_MAPPING['Basic'];
  };

  // Check if user has access to specific tab
  const hasTabAccess = (tabName) => {
    const allowedTabs = getAllowedTabs(userRole);
    return allowedTabs.includes(tabName);
  };

  // Check if user is admin
  const isAdmin = () => {
    return ['Admin', 'Dev'].includes(userRole);
  };

  // Function to log user login following the Python pattern
  const logUserLogin = async (accessToken, userData) => {
    try {
      // Get the tai-khoan API URL
      const isLocal = window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1' || 
                      process.env.NODE_ENV === 'development';
      
      // In development, connect directly to the remote server
      // In production, use the nginx proxy
      const apiUrl = isLocal 
        ? 'http://64.225.20.106:8081'  // Development - direct connection to remote server
        : '/tai-khoan';  // Production - nginx proxy to tai-khoan-container

      // Get IP address (non-blocking)
      let ipAddress = 'localhost-dev';
      // Skip IP fetching in development to avoid CORS issues
      if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
        try {
          const ipResponse = await fetch('https://api64.ipify.org?format=json', {
            mode: 'cors'
          });
          const ipData = await ipResponse.json();
          ipAddress = ipData.ip;
        } catch (ipError) {
          console.warn('Could not fetch IP address:', ipError);
          ipAddress = 'unknown-ip';
        }
      }

      // Generate session ID (similar to Python function)
      const sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);

      // Prepare the payload following the Python log_user_login function pattern
      const payload = {
        user_id: userData?.id || user?.sub || 'unknown',
        ip_address: ipAddress,
        user_agent: navigator.userAgent,
        session_id: sessionId
      };

      console.log('[UserContext] Logging user login with payload:', payload);

      // Make the API call to /log_user_login endpoint
      const response = await fetch(`${apiUrl}/log_user_login`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('[UserContext] User login logged successfully:', result);
        return result;
      } else {
        const errorText = await response.text();
        console.warn('[UserContext] Failed to log user login:', response.status, errorText);
      }
    } catch (error) {
      console.error('[UserContext] Error logging user login:', error);
      throw error;
    }
  };

  // Clean up stale Auth0 cache entries (fixes slow login issue)
  const cleanupStaleAuth0Cache = () => {
    try {
      // Get all Auth0 SPA cache keys
      const auth0Keys = Object.keys(localStorage).filter(key => 
        key.includes('@@auth0spajs@@')
      );
      
      if (auth0Keys.length > 1) {
        console.log(`[UserContext] Found ${auth0Keys.length} Auth0 cache entries, cleaning up stale ones...`);
        
        // Parse each entry to find the most recent one
        let mostRecentKey = null;
        let mostRecentTime = 0;
        
        auth0Keys.forEach(key => {
          try {
            const value = localStorage.getItem(key);
            if (value) {
              const data = JSON.parse(value);
              // Check for expiry or last updated time
              const expiresAt = data.body?.expires_at || data.expiresAt || 0;
              if (expiresAt > mostRecentTime) {
                mostRecentTime = expiresAt;
                mostRecentKey = key;
              }
            }
          } catch (e) {
            // If we can't parse it, it's probably corrupted
            console.log(`[UserContext] Removing corrupted Auth0 cache: ${key}`);
            localStorage.removeItem(key);
          }
        });
        
        // Remove all except the most recent
        auth0Keys.forEach(key => {
          if (key !== mostRecentKey) {
            console.log(`[UserContext] Removing stale Auth0 cache: ${key}`);
            localStorage.removeItem(key);
          }
        });
      }
      
      // Also clean up expired tokens
      ['access_token', 'id_token'].forEach(tokenKey => {
        const token = localStorage.getItem(tokenKey);
        if (token && token.includes('.')) {
          try {
            const parts = token.split('.');
            if (parts.length === 3) {
              const payload = JSON.parse(atob(parts[1]));
              if (payload.exp) {
                const expDate = new Date(payload.exp * 1000);
                if (expDate < new Date()) {
                  console.log(`[UserContext] Removing expired ${tokenKey}`);
                  localStorage.removeItem(tokenKey);
                }
              }
            }
          } catch (e) {
            // Invalid token format
          }
        }
      });
      
    } catch (error) {
      console.warn('[UserContext] Error during Auth0 cache cleanup:', error);
      // Don't fail authentication if cleanup fails
    }
  };

  // Track if we've already loaded user data to prevent re-runs
  const [hasLoadedUser, setHasLoadedUser] = useState(false);

  // Load user data on authentication
  useEffect(() => {
    console.log('[UserContext] useEffect triggered - Auth state:', { 
      isAuthenticated, 
      hasUser: !!user, 
      userId: user?.sub,
      hasLoadedUser 
    });
    
    const loadUserData = async () => {
      console.log('[UserContext] loadUserData function called');
      
      if (!isAuthenticated || !user || hasLoadedUser) {
        console.log('[UserContext] Early return because:', {
          notAuthenticated: !isAuthenticated,
          noUser: !user,
          alreadyLoaded: hasLoadedUser
        });
        setLoading(false);
        return;
      }

      try {
        console.log('[UserContext] Starting user data load...');
        setLoading(true);
        setError(null);

        // Clean up stale Auth0 cache before getting new token
        cleanupStaleAuth0Cache();

        // Get access token
        const accessToken = await getAccessTokenSilently();
        
        // Store token for API calls
        localStorage.setItem('access_token', accessToken);

        // Fetch user data from API with longer timeout since tai-khoan is slow
        const userResponse = await Promise.race([
          fetchUserData(accessToken),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('User data fetch timeout')), 16000) // 16 seconds max - give tai-khoan time
          )
        ]);
        
        // Determine role
        const role = determineUserRole(userResponse);

        // Update state
        setUserData(userResponse);
        setUserRole(role);
        setHasLoadedUser(true); // Mark as loaded to prevent re-runs

        console.log(`[UserContext] User context loaded successfully - Role: ${role}, Allowed tabs: [${getAllowedTabs(role).join(', ')}]`);

        // Log user session after successful authentication
        try {
          console.log('[UserContext] Logging user session...');
          const sessionResult = await sessionLogger.logUserSession(accessToken);
          console.log('[UserContext] Session logging result:', sessionResult);
        } catch (sessionError) {
          console.error('[UserContext] Failed to log session:', sessionError);
          // Don't fail the login if session logging fails
        }

      } catch (err) {
        console.error(`[UserContext] Failed to load user data for ${user?.email || 'unknown'}:`, err.message);
        console.error('[UserContext] Full error:', err);
        setError(err.message);
        
        // Check if it's a timeout - if so, maybe tai-khoan is just slow
        if (err.message.includes('timeout')) {
          console.warn('[UserContext] Tai-khoan timeout - defaulting to Basic role. The service might be slow.');
        }
        
        // Fallback to basic role
        setUserRole('Basic');
      } finally {
        console.log('[UserContext] Finally block - setting loading to false');
        setLoading(false);
      }
    };

    loadUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.sub]); // Only depend on auth state and user ID, not the function

  // Debug component to show user role info
  const DebugUserInfo = () => {
    if (!userData) return null;

    if (!debugInfoVisible) {
      // Show a small toggle button when debug info is hidden
      return (
        <button
          onClick={() => setDebugInfoVisible(true)}
          className="fixed bottom-4 right-4 bg-gray-600 hover:bg-gray-700 text-white p-2 rounded-full text-xs z-50 transition-colors"
          title="Show debug info (Ctrl+D)"
        >
          ?
        </button>
      );
    }

    return (
      <div className="fixed bottom-4 right-4 bg-gray-800 text-white p-3 rounded-lg text-xs z-50 max-w-xs">
        <div className="flex justify-between items-center mb-1">
          <div className="font-bold">User Debug Info</div>
          <button
            onClick={() => setDebugInfoVisible(false)}
            className="text-gray-300 hover:text-white ml-2 p-1 rounded hover:bg-gray-700 transition-colors"
            title="Close debug info (Ctrl+D)"
          >
            ×
          </button>
        </div>
        <div>ID: {userData.id}</div>
        <div>Email: {userData.email}</div>
        <div>DB Role: {userData.user_role}</div>
        <div>Plan Status: {userData.plan_status}</div>
        <div>Plan ID: {userData.plan_id}</div>
        <div className="font-bold text-yellow-300">Final Role: {userRole}</div>
        <div className="text-xs mt-1">
          Tabs: {getAllowedTabs(userRole).join(', ')}
        </div>
        <div className="text-xs mt-2 text-gray-400 border-t border-gray-600 pt-1">
          Press Ctrl+D to toggle
        </div>
      </div>
    );
  };

  const value = {
    // User data
    userData,
    userRole,
    loading,
    error,

    // Auth helpers
    isAuthenticated,
    user,

    // Role checking functions
    getAllowedTabs: () => getAllowedTabs(userRole),
    hasTabAccess,
    isAdmin,

    // Debug component and controls
    DebugUserInfo,
    debugInfoVisible,
    toggleDebugInfo: () => setDebugInfoVisible(!debugInfoVisible),
    showDebugInfo: () => setDebugInfoVisible(true),
    hideDebugInfo: () => setDebugInfoVisible(false),

    // Raw data for debugging
    _debug: {
      userData,
      userRole,
      allowedTabs: getAllowedTabs(userRole)
    }
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;